/** @jsx jsx */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import { jsx } from '@emotion/core';
import { Button, Divider } from '@chakra-ui/core';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { body, footer, popover, trigger, arrow } from './confirmPopover.style';

type Position = {
  top: number;
  left: number;
  arrowRight: number;
};

interface ConfirmPopoverProps {
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (e: React.MouseEvent) => void;
  onCancel?: () => void;
  children: ReactNode;
}

export const ConfirmPopover = React.memo(function ConfirmPopover({
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  children,
}: ConfirmPopoverProps) {
  const { t } = useTranslation('common');
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState<Position | null>(null);
  const [visible, setVisible] = useState(false);

  const open = useCallback(() => {
    const triggerEl = triggerRef.current;
    /* istanbul ignore next */
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();

    const popoverWidth = 260; // MUST match CSS width
    const triggerCenterX = rect.left + rect.width / 2;

    const popoverLeft = rect.right - popoverWidth;
    const arrowRight = popoverWidth - (triggerCenterX - popoverLeft);

    setPos({
      top: rect.bottom,
      left: rect.right,
      arrowRight,
    });

    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    onCancel?.();
    setTimeout(() => setPos(null), 150);
  }, [onCancel]);

  const confirm = useCallback(
    (e: React.MouseEvent) => {
      setVisible(false);
      setTimeout(() => {
        setPos(null);
        onConfirm(e);
      }, 150);
    },
    [onConfirm],
  );

  // outside click / esc
  useEffect(() => {
    if (!pos) return;

    const onMouseDown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        close();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);

    // eslint-disable-next-line
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pos, close]);

  return (
    <React.Fragment>
      <span ref={triggerRef} css={trigger} onClick={open}>
        {children}
      </span>

      {pos &&
        createPortal(
          <div
            ref={popoverRef}
            css={popover(visible ? 'open' : 'close')}
            style={{
              top: pos.top,
              left: pos.left,
              transform: 'translate(-100%, 8px)', // bottom-end
            }}
          >
            <div css={arrow} />
            <div css={body}>{message}</div>
            <Divider />
            <div css={footer}>
              <Button size="sm" variant="outline" onClick={close}>
                {cancelText || t('confirmPopover.cancel')}
              </Button>
              <Button size="sm" colorScheme="red" onClick={confirm}>
                {confirmText || t('confirmPopover.confirm')}
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </React.Fragment>
  );
});
